import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Crop, Image as ImageIcon, Minus, Plus, X } from 'lucide-react';

interface Props {
  file: File;
  width: number;
  height: number;
  purpose?: 'pattern' | 'background';
  onCancel: () => void;
  onKeepOriginal: () => void;
  onConfirm: (file: File) => void;
}

export function ImageCropModal({ file, width, height, purpose = 'pattern', onCancel, onKeepOriginal, onConfirm }: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);
  const [url, setUrl] = useState('');
  const [natural, setNatural] = useState({ width: 1, height: 1 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  const viewportSize = useMemo(() => {
    const ratio = width / height;
    const maxWidth = Math.min(620, Math.max(280, window.innerWidth - 80));
    const maxHeight = Math.min(430, Math.max(240, window.innerHeight - 310));
    if (maxWidth / maxHeight > ratio) return { width: Math.max(90, maxHeight * ratio), height: maxHeight };
    return { width: maxWidth, height: Math.max(90, maxWidth / ratio) };
  }, [height, width]);

  const baseScale = Math.max(viewportSize.width / natural.width, viewportSize.height / natural.height);
  const displayWidth = natural.width * baseScale * zoom;
  const displayHeight = natural.height * baseScale * zoom;
  const maxX = Math.max(0, (displayWidth - viewportSize.width) / 2);
  const maxY = Math.max(0, (displayHeight - viewportSize.height) / 2);
  const clamped = { x: Math.max(-maxX, Math.min(maxX, offset.x)), y: Math.max(-maxY, Math.min(maxY, offset.y)) };

  function pointerDown(event: React.PointerEvent) {
    dragRef.current = { x: event.clientX, y: event.clientY, startX: clamped.x, startY: clamped.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function pointerMove(event: React.PointerEvent) {
    if (!dragRef.current) return;
    const nextX = dragRef.current.startX + event.clientX - dragRef.current.x;
    const nextY = dragRef.current.startY + event.clientY - dragRef.current.y;
    setOffset({ x: Math.max(-maxX, Math.min(maxX, nextX)), y: Math.max(-maxY, Math.min(maxY, nextY)) });
  }

  async function confirm() {
    const scale = baseScale * zoom;
    const sourceWidth = viewportSize.width / scale;
    const sourceHeight = viewportSize.height / scale;
    const sourceX = (natural.width - sourceWidth) / 2 - clamped.x / scale;
    const sourceY = (natural.height - sourceHeight) / 2 - clamped.y / scale;
    const image = new Image();
    image.src = url;
    await image.decode();
    const longest = Math.min(2048, Math.max(natural.width, natural.height));
    const targetScale = longest / Math.max(sourceWidth, sourceHeight);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sourceWidth * targetScale));
    canvas.height = Math.max(1, Math.round(sourceHeight * targetScale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png', 0.94));
    if (blob) onConfirm(new File([blob], file.name.replace(/\.[^.]+$/, '') + '-裁切.png', { type: 'image/png' }));
  }

  return (
    <div className="modal-backdrop crop-backdrop" role="dialog" aria-modal="true" aria-labelledby="crop-title">
      <div className="crop-modal">
        <button className="icon-button modal-close" type="button" onClick={onCancel} aria-label="关闭裁切"><X size={18} /></button>
        <p className="eyebrow">生成前调整</p>
        <h2 id="crop-title"><Crop size={22} />{purpose === 'background' ? '裁切参考底图' : '裁切图片'}</h2>
        <p>拖动图片调整位置，再用滑杆放大。裁切框比例与底板一致。</p>
        <div
          ref={viewportRef}
          className="crop-viewport"
          style={{ width: viewportSize.width, height: viewportSize.height }}
          onPointerDown={pointerDown}
          onPointerMove={pointerMove}
          onPointerUp={() => { dragRef.current = null; }}
          onPointerCancel={() => { dragRef.current = null; }}
        >
          {url && <img src={url} alt="待裁切图片" draggable={false} onLoad={(event) => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} style={{ width: displayWidth, height: displayHeight, transform: `translate(calc(-50% + ${clamped.x}px), calc(-50% + ${clamped.y}px))` }} />}
          <span className="crop-grid" aria-hidden="true" />
        </div>
        <div className="crop-zoom"><Minus size={16} /><input aria-label="裁切缩放" type="range" min="1" max="3" step="0.01" value={zoom} onChange={(event) => { setZoom(Number(event.target.value)); setOffset({ x: 0, y: 0 }); }} /><Plus size={16} /><strong>{Math.round(zoom * 100)}%</strong></div>
        <div className="crop-actions">
          <button className="secondary-button" type="button" onClick={onKeepOriginal}><ImageIcon size={17} />{purpose === 'background' ? '保留整张底图' : '保留全图'}</button>
          <button className="primary-button" type="button" onClick={confirm}><Check size={17} />{purpose === 'background' ? '使用裁切底图' : '使用裁切结果'}</button>
        </div>
      </div>
    </div>
  );
}
